import { useState } from 'react'
import { Search, ChevronDown } from 'lucide-react'

const speciesOptions = ['Dog', 'Cat', 'Bird', 'Reptile', 'Horse', 'Other'] as const

const statusOptions = ['Active', 'Inactive', 'Deceased'] as const

interface PatientSearchProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  speciesFilter: string
  onSpeciesFilterChange: (species: string) => void
  statusFilter: string
  onStatusFilterChange: (status: string) => void
}

export function PatientSearch({
  searchQuery,
  onSearchChange,
  speciesFilter,
  onSpeciesFilterChange,
  statusFilter,
  onStatusFilterChange,
}: PatientSearchProps) {
  const [speciesDropdownOpen, setSpeciesDropdownOpen] = useState(false)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)

  return (
    <div data-testid="patient-search" className="flex items-center gap-3 flex-wrap">
      {/* Search bar */}
      <div className="relative flex-1 min-w-[200px] max-w-[360px]">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          data-testid="patient-search-input"
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by patient name or owner name..."
          className="w-full h-7 pl-7 pr-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 placeholder:text-text-disabled"
        />
      </div>

      {/* Species filter dropdown */}
      <div className="relative">
        <button
          data-testid="patient-species-filter"
          onClick={() => {
            setSpeciesDropdownOpen(!speciesDropdownOpen)
            setStatusDropdownOpen(false)
          }}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Species:</span>
          {speciesFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {speciesDropdownOpen && (
          <div
            data-testid="patient-species-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[160px] max-h-48 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="species-option-all"
              onClick={() => {
                onSpeciesFilterChange('')
                setSpeciesDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {speciesOptions.map((sp) => (
              <button
                key={sp}
                type="button"
                data-testid={`species-option-${sp}`}
                onClick={() => {
                  onSpeciesFilterChange(sp)
                  setSpeciesDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {sp}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status filter dropdown */}
      <div className="relative">
        <button
          data-testid="patient-status-filter"
          onClick={() => {
            setStatusDropdownOpen(!statusDropdownOpen)
            setSpeciesDropdownOpen(false)
          }}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Status:</span>
          {statusFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {statusDropdownOpen && (
          <div
            data-testid="patient-status-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[140px] max-h-40 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="status-option-all"
              onClick={() => {
                onStatusFilterChange('')
                setStatusDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {statusOptions.map((st) => (
              <button
                key={st}
                type="button"
                data-testid={`status-option-${st}`}
                onClick={() => {
                  onStatusFilterChange(st)
                  setStatusDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {st}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
